import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';
import { FileManagerUploadedFilesComponent } from '@features/file-manager-uploaded-files/file-manager-uploaded-files.component';
import { ProtocolVersionDropdownComponent } from '@features/protocol-version-dropdown/protocol-version-dropdown.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileManagerErrorMessageComponent } from '@shared/components/file-manager-error-message/file-manager-error-message.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ADMIN_METADATA_ID } from '@shared/constants/uuid';
import { ApiService } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DownloadTemplateButtonComponent,
    ProtocolVersionDropdownComponent,
    ReactiveFormsModule,
    FileManagerComponent,
    FileManagerUploadedFilesComponent,
    FileManagerErrorMessageComponent,
  ],
  selector: 'aux-bulk-upload-site-invoiceables-modal',
  template: `
    <div class="flex flex-col gap-4">
      <aux-file-manager-error-message [errorMessage]="errorMessage()" />
      <aux-protocol-version-dropdown
        [formControl]="protocolVersion"
        [clearable]="true"
        label="Protocol Version (Optional)"
      />
      <aux-download-template-button
        label="Download the template"
        [templateType]="uploadSiteInvoiceablesTemplateType"
        class="mb-4"
        [showIcon]="false"
        [templatePayload]="templatePayload()"
      />
      <aux-file-manager
        #fileManager
        class="h-48"
        [pathFn]="pathFn"
        [eager]="false"
        [extensions]="extensions"
        [accept]="'.csv'"
        [showSpecificError]="true"
      />
      <aux-file-manager-uploaded-files
        [fileManager]="fileManager"
        [titleLess]="true"
        [borderLess]="false"
      />
    </div>
  `,
})
export class BulkUploadSiteInvoiceablesModalComponent {
  apiService = inject(ApiService);
  eventService = inject(EventService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  ref = inject(CustomOverlayRef<unknown, unknown>);

  errorMessage = signal('');

  protocolVersion = new FormControl<null | string>(null);

  selectedProtocol = toSignal(this.protocolVersion.valueChanges);

  fileManager = viewChild<FileManagerComponent>('fileManager');

  filesUploaded = computed(() => this.fileManager()?.fileQuery.count());
  templatePayload = computed(() => ({
    patient_protocol_version_id: this.selectedProtocol(),
  }));
  uploadDisabled = computed(() => !this.filesUploaded() || !!this.errorMessage());

  extensions = [Utils.CSV_FILE_EXTENSION];
  uploadSiteInvoiceablesTemplateType = TemplateType.BULK_SITE_INVOICEABLE_TEMPLATE;

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager()) {
      const files = this.fileManager()!.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      const file = files[0];

      const trialId = this.mainQuery.getValue().trialKey;

      const fileSuccess = await this.fileManager()?.fileService.uploadFiles({
        metadata: { admin: ADMIN_METADATA_ID },
      });

      const filePath = `public/${file.bucket_key}`;

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${file.bucket_key}`,
            entity_id: trialId,
            entity_type: EntityType.TRIAL,
            type: EventType.BULK_SITE_INVOICEABLE_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success('Site Invoiceables updated');
        } else {
          void this.apiService.removeFile(filePath);
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }
  }

  pathFn = () => {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/site-invoiceables/`;
  };
}
